import React, { useState } from "react";
import HeadingBanner, { HeadingBannerDashboard } from "../Components/HeadingBanner.jsx";
import Dashboard from "../Components/Dashboard.jsx";
import instance from "../Config/Config.js";
import toast from "react-hot-toast";
import { FaCheckCircle, FaCloudUploadAlt, FaMobileAlt } from "react-icons/fa";

const SCHEMES = [
  {
    key: "saving_plan",
    title: "Chit Jewel Savings Plan",
    desc: "Monthly Chit Scheme Savings",
  },
  {
    key: "digital_plan",
    title: "Digi Gold SIP Plan",
    desc: "Digital 24K Gold SIP Plan",
  },
  {
    key: "gold_plant",
    title: "Digi Gold Plant Scheme",
    desc: "Gold Plant Investment Scheme",
  },
  {
    key: "digital_silver_plan",
    title: "Digi Silver SIP Plan",
    desc: "Digital Pure Silver SIP Plan",
  },
];

const SubmitPaymentProof = ({ settings, image }) => {
  const [selectedScheme, setSelectedScheme] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [successTxn, setSuccessTxn] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload a valid image file (PNG/JPEG/WEBP).");
        return;
      }
      setScreenshotFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setSuccessTxn(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!screenshotFile) {
      toast.error("Please select or upload your payment screenshot first.");
      return;
    }

    if (!selectedScheme) {
      toast.error("Please choose a scheme for this payment.");
      return;
    }

    const formData = new FormData();
    formData.append("scheme_type", selectedScheme);
    formData.append("payment_screenshot", screenshotFile);

    setLoading(true);
    try {
      const response = await instance.post("submit_shared_payment_proof", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response?.data?.status) {
        toast.success(response.data.message || "Payment proof submitted successfully!");
        setSuccessTxn(response.data.transaction_id || "Pending Verification");
        setScreenshotFile(null);
        setPreviewUrl("");
        setSelectedScheme("");
      } else {
        toast.error(response?.data?.message || "Failed to submit payment proof.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Error uploading payment proof. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <HeadingBannerDashboard image={image} />
      <section className="py-4">
        <div className="container">
          <div className="mydashboard_layout">
            <div className="row">
              <div className="col-xl-3 col-lg-4 col-sm-12 mb-4">
                <Dashboard setting_data={settings} />
              </div>

              <div className="col-xl-9 col-lg-8 col-sm-12">
                <div className="card shadow-sm border-0 rounded-4 p-4 p-md-5">
                  <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
                    <div>
                      <h3 className="fw-bold mb-1" style={{ color: "#222" }}>
                        Submit Payment Proof
                      </h3>
                      <p className="text-muted small mb-0">
                        Upload your Google Pay / UPI screenshot and assign it to your scheme for instant Admin approval.
                      </p>
                    </div>
                  </div>

                  {successTxn && (
                    <div className="alert alert-success d-flex align-items-center p-3 mb-4 rounded-3" role="alert">
                      <FaCheckCircle className="text-success me-3 fs-3" />
                      <div>
                        <strong>Proof Submitted Successfully!</strong>
                        <div className="small">Transaction Ref: <code>{successTxn}</code>. Our Admin is verifying your payment.</div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row g-4 align-items-center">
                      {/* Left: Mock Phone Preview */}
                      <div className="col-lg-5 text-center">
                        <label className="fw-semibold text-dark mb-2 d-block text-start">
                          Selected Payment Screenshot:
                        </label>
                        <div
                          className="mx-auto position-relative"
                          style={{
                            width: "240px",
                            height: "420px",
                            backgroundColor: "#111",
                            borderRadius: "32px",
                            border: "6px solid #2b2b2b",
                            boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {previewUrl ? (
                            <img
                              src={previewUrl}
                              alt="Payment Proof Preview"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <div className="text-center p-3 text-white-50">
                              <FaMobileAlt size={48} className="mb-2 text-secondary" />
                              <p className="small mb-1 text-white">GPay Screenshot Preview</p>
                              <span className="text-muted" style={{ fontSize: "11px" }}>
                                Click "Choose Image" below
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="mt-3">
                          <label
                            htmlFor="proofFileInput"
                            className="btn btn-sm btn-outline-secondary px-3 py-2 rounded-pill"
                            style={{ cursor: "pointer" }}
                          >
                            <FaCloudUploadAlt className="me-2" />
                            {previewUrl ? "Change Screenshot" : "Choose Screenshot"}
                          </label>
                          <input
                            type="file"
                            id="proofFileInput"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                          />
                        </div>
                      </div>

                      {/* Right: Scheme Selection & Submit Button */}
                      <div className="col-lg-7">
                        <label className="fw-bold text-dark mb-3 d-block fs-5">
                          Select Scheme:
                        </label>

                        <div className="row g-3">
                          {SCHEMES.map((scheme) => {
                            const isSelected = selectedScheme === scheme.key;
                            return (
                              <div className="col-sm-6" key={scheme.key}>
                                <div
                                  onClick={() => setSelectedScheme(scheme.key)}
                                  className={`p-3 rounded-4 border text-center h-100 d-flex flex-column justify-content-center transition-all ${
                                    isSelected
                                      ? "border-2 border-success bg-success-subtle shadow-sm"
                                      : "border-light-subtle bg-white hover-shadow"
                                  }`}
                                  style={{
                                    cursor: "pointer",
                                    minHeight: "110px",
                                    transition: "all 0.2s ease-in-out",
                                    borderColor: isSelected ? "#5ca356" : "#e0e0e0",
                                    backgroundColor: isSelected ? "#f2f9f1" : "#ffffff",
                                  }}
                                >
                                  <h6
                                    className="fw-bold mb-1"
                                    style={{
                                      color: isSelected ? "#478942" : "#333",
                                      fontSize: "14px",
                                    }}
                                  >
                                    {scheme.title}
                                  </h6>
                                  <span className="text-muted" style={{ fontSize: "11px" }}>
                                    {scheme.desc}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="mt-4 pt-2">
                          <button
                            type="submit"
                            disabled={loading}
                            className="btn w-100 py-3 text-white fw-bold rounded-3 shadow-sm"
                            style={{
                              backgroundColor: "#5ca356",
                              fontSize: "16px",
                              letterSpacing: "0.3px",
                              opacity: loading ? 0.7 : 1,
                            }}
                          >
                            {loading ? "Submitting Payment Proof..." : "Submit Payment Proof"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SubmitPaymentProof;
